import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios';
import { store } from '../store';
import { performLogout } from './logoutService';
import { API_URL } from '../config/api.config';
import toast from '../utils/toast';
import { getExtendedToken } from './appAuthService';
import { handleDRFValidationErrors } from '@/utils/errorParser';
import { STORAGE_KEYS } from '../utils/storageKeys';

// Utility: Extract DRF-style error message
function extractErrorMessage(data: unknown): string | null {
  if (!data) return null;
  if (typeof data === 'string') {
    // Ignore HTML responses
    if (data.trim().startsWith('<')) return null;
    return data;
  }
  if (typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (typeof obj.detail === 'string') return obj.detail;
  }
  return null;
}

// Enhanced error response type matching backend exception handler
interface BackendErrorResponse {
  detail?: string;
  error_type?: string;
  status_code?: number;
  non_field_errors?: string[];
  [field: string]: any;
}

// Global error handler (aligned with backend exception handler)
function handleErrorResponse(error: AxiosError) {
  if (!error.response) {
    toast.error('Network error. Please check your connection.', 3000);
    return;
  }

  const { status, data } = error.response;
  const responseData = data as BackendErrorResponse;
  const message = extractErrorMessage(responseData);

  // Log error details for debugging (matches backend error_type and status_code)
  if (responseData?.error_type) {
    console.error(`Backend Error [${responseData.error_type}]:`, responseData);
  }

  // Account deactivated by an admin - block access, tell the user, and force
  // a full logout so they land back on the Auth0 hosted login page.
  if (responseData?.error_type === 'account_inactive') {
    toast.error(
      message || 'This account is inactive. Please contact your administrator.',
      5000
    );
    logoutFromAuth0();
    return;
  }

  // Handle DRF validation structure first (400 status)
  if (status === 400 && responseData && typeof responseData === 'object') {
    const handled = handleDRFValidationErrors(responseData);
    if (handled) return;
  }

  switch (status) {
    case 400:
      toast.error(message || 'Bad request. Please check your input.', 3000);
      break;

    case 401: {
      const failureCount = parseInt(
        sessionStorage.getItem(STORAGE_KEYS.AUTH_FAILURE_COUNT) || '0',
        10
      );

      sessionStorage.setItem(
        STORAGE_KEYS.AUTH_FAILURE_COUNT,
        (failureCount + 1).toString()
      );

      if (failureCount >= 2) {
        toast.error(
          message || 'Session expired. Please log in again.',
          3000
        );
        sessionStorage.removeItem(STORAGE_KEYS.AUTH_FAILURE_COUNT);
        performLogout();
      } else {
        toast.warning('Authentication issue detected.', 2000);
      }
      break;
    }

    case 403:
      toast.error(
        message || 'You do not have permission to perform this action.',
        3000
      );
      break;

    case 404:
      toast.error(
        message || 'The requested resource was not found.',
        3000
      );
      break;

    case 429:
      toast.error(
        message || 'Too many requests. Please try again later.',
        3000
      );
      break;

    default:
      if (status >= 500) {
        toast.error(
          message || 'Server error occurred. Please try again later.',
          3000
        );
      } else {
        toast.error(message || 'An error occurred. Please try again', 3000);
      }
  }
}

  // Axios instances
const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

export const apiFormData = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'multipart/form-data' }
});

  // Request interceptor
const authRequestInterceptor = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  const extendedToken = getExtendedToken();
  const reduxToken = store.getState().auth.accessToken;
  const accessToken =
    extendedToken || reduxToken || localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

  if (accessToken) {
    config.headers = config.headers ?? {}; // ensure headers exist
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  if (config.data instanceof FormData) {
    delete config.headers?.['Content-Type'];
  }

  return config;
};

  // Attach interceptors
api.interceptors.request.use(authRequestInterceptor);
apiFormData.interceptors.request.use(authRequestInterceptor);

api.interceptors.response.use(
  (response: AxiosResponse) => {
    sessionStorage.removeItem(STORAGE_KEYS.AUTH_FAILURE_COUNT);
    return response;
  },
  (error: AxiosError) => {
    handleErrorResponse(error);
    return Promise.reject(error);
  }
);

apiFormData.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    handleErrorResponse(error);
    return Promise.reject(error);
  }
);

export default api;
